import React from 'react';
import { usePortalLanguage } from '../../../contexts/PortalLanguageContext';
import { formatCurrencyWhole } from '../../../utils/formatting';
import { formatClientPortalDateOnly, CLIENT_PORTAL_DATE_FIELDS } from '../../../utils/clientPortal';

function Row({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-4 py-1.5">
            <span className="text-sm text-[color:var(--text-muted)]">{label}</span>
            <span className="text-sm font-semibold text-[color:var(--text-primary)] text-right">{value}</span>
        </div>
    );
}

function Group({ title, rows }) {
    if (rows.length === 0) return null;
    return (
        <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)]">{title}</h3>
            <div className="mt-2 divide-y divide-[color:var(--border-color)]">
                {rows.map(r => <Row key={r.label} label={r.label} value={r.value} />)}
            </div>
        </div>
    );
}

function PortalDetailsCard({ transaction, isCompactLayout }) {
    const { t: tr, locale } = usePortalLanguage();
    const t = transaction || {};
    const isSeller = t.transactionType === 'Seller';
    const priceRaw = isSeller ? (t.listPrice || t.salePrice) : (t.salePrice || t.listPrice);

    const money = (v) => (v ? formatCurrencyWhole(v) : '');
    const financialRows = [
        { label: tr(isSeller ? 'List price' : 'Price'), value: money(priceRaw) },
        { label: tr('Earnest money'), value: money(t.earnestMoneyAmount) },
        { label: tr('Seller concessions'), value: money(t.sellerConcessions) },
        { label: tr('Home warranty'), value: money(t.homeWarrantyAmount) }
    ].filter(r => r.value);

    const dateRows = CLIENT_PORTAL_DATE_FIELDS
        .map(({ key, label }) => ({ label: tr(label), value: t[key] ? formatClientPortalDateOnly(t[key], { emptyLabel: '', locale }) : '' }))
        .filter(r => r.value);

    if (financialRows.length === 0 && dateRows.length === 0) return null;

    return (
        <div className={`rounded-2xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-sm ${isCompactLayout ? 'p-5' : 'p-6'}`}>
            <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{tr('Transaction details')}</h2>
            <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                <Group title={tr('Financial terms')} rows={financialRows} />
                <Group title={tr('Contract & contingency dates')} rows={dateRows} />
            </div>
        </div>
    );
}

export default PortalDetailsCard;
